import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { 
  Routing, RoutingCreate, RoutingFilter, RoutingOperation, 
  RoutingOperationCreate, RoutingStatus, RoutingSummary, RoutingOperationWithDetails,
  RoutingComponentOperation, RoutingComponentOperationCreate, RoutingComponentOperationWithDetails,
  ComponentWithOperations
} from '@/types/routing';
import { UUID } from '@/types/common';

interface RoutingState {
  routings: RoutingSummary[];
  currentRouting: RoutingSummary | null;
  routingOperations: RoutingOperationWithDetails[];
  componentOperations: RoutingComponentOperationWithDetails[];
  isLoading: boolean;
  error: string | null;
  
  // Routing CRUD operations
  fetchRoutings: (filters?: RoutingFilter) => Promise<void>;
  getRoutingById: (id: UUID) => Promise<RoutingSummary | null>;
  createRouting: (routing: RoutingCreate) => Promise<UUID | null>;
  updateRouting: (id: UUID, updates: Partial<Routing>) => Promise<boolean>;
  updateRoutingStatus: (id: UUID, status: RoutingStatus) => Promise<boolean>;
  deleteRouting: (id: UUID) => Promise<boolean>;
  duplicateRouting: (id: UUID, userId: UUID) => Promise<UUID | null>;
  
  // Routing Operations CRUD
  fetchRoutingOperations: (routingId: UUID) => Promise<void>;
  createRoutingOperation: (operation: RoutingOperationCreate) => Promise<UUID | null>;
  updateRoutingOperation: (id: UUID, updates: Partial<RoutingOperation>) => Promise<boolean>;
  deleteRoutingOperation: (id: UUID) => Promise<boolean>;
  reorderRoutingOperations: (routingId: UUID, sequenceUpdates: { id: UUID, sequence_no: number }[]) => Promise<boolean>;
  
  // Component Operations CRUD
  fetchComponentOperationsByRouting: (routingId: UUID) => Promise<RoutingComponentOperationWithDetails[]>;
  createComponentOperation: (data: RoutingComponentOperationCreate) => Promise<UUID | null>;
  deleteComponentOperation: (id: UUID) => Promise<boolean>;
  getOperationsGroupedByComponent: (routingId: UUID) => ComponentWithOperations[];
  
  // State management
  clearCurrentRouting: () => void;
  setError: (error: string | null) => void;
}

export const useRoutingStore = create<RoutingState>((set, get) => ({
  routings: [],
  currentRouting: null,
  routingOperations: [],
  componentOperations: [],
  isLoading: false,
  error: null,
  
  // Fetch routings with optional filters
  fetchRoutings: async (filters: RoutingFilter = {}) => {
    set({ isLoading: true, error: null });
    
    try {
      // Create a timeout promise to ensure we don't get stuck in loading state
      const timeoutId = setTimeout(() => {
        console.warn('Timeout fetching routings, forcing completion');
        set({ isLoading: false, routings: [], error: 'Failed to load routings in time. Please try again.' });
      }, 5000); // Reduced timeout for better user experience
      
      let query = supabase
        .from('routings_summary')
        .select('*');
      
      // Apply filters
      if (filters.search) {
        query = query.ilike('routing_name', `%${filters.search}%`);
      }
      
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      
      if (filters.project_id && filters.project_id !== 'all') {
        query = query.eq('project_id', filters.project_id);
      }
      
      if (filters.bom_id && filters.bom_id !== 'all') {
        query = query.eq('bom_id', filters.bom_id);
      }
      
      if (filters.assembly_id) {
        query = query.eq('assembly_id', filters.assembly_id);
      }
      
      if (filters.created_by) {
        query = query.eq('created_by', filters.created_by);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      // Clear the timeout since we got a response
      clearTimeout(timeoutId);
      
      if (error) throw error;
      
      // Always set as an array, even if null or undefined is returned
      set({ routings: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching routings:', error);
      set({ error: (error as Error).message, routings: [], isLoading: false });
    }
  },
  
  // Get routing by ID
  getRoutingById: async (id: UUID) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('routings_summary')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      set({ currentRouting: data, isLoading: false });
      
      return data;
    } catch (error) {
      console.error('Error fetching routing by ID:', error);
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },
  
  // Create a new routing
  createRouting: async (routing: RoutingCreate) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('routings')
        .insert(routing)
        .select('id')
        .single();
      
      if (error) throw error;
      
      // Refresh routings list
      await get().fetchRoutings();
      
      set({ isLoading: false });
      return data.id;
    } catch (error) {
      console.error('Error creating routing:', error);
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },
  
  // Update routing
  updateRouting: async (id: UUID, updates: Partial<Routing>) => {
    set({ isLoading: true, error: null });
    
    try {
      const { error } = await supabase
        .from('routings')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      
      if (error) throw error;
      
      // Refresh routings list and current routing
      await get().fetchRoutings();
      
      if (get().currentRouting?.id === id) {
        await get().getRoutingById(id);
      }
      
      set({ isLoading: false });
      return true;
    } catch (error) {
      console.error('Error updating routing:', error);
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },
  
  // Update routing status
  updateRoutingStatus: async (id: UUID, status: RoutingStatus) => {
    return await get().updateRouting(id, { status });
  },
  
  // Delete routing
  deleteRouting: async (id: UUID) => {
    set({ isLoading: true, error: null });
    
    try {
      const { error } = await supabase
        .from('routings')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Remove from local state
      set(state => ({
        routings: state.routings.filter(routing => routing.id !== id),
        currentRouting: state.currentRouting?.id === id ? null : state.currentRouting,
        isLoading: false
      }));
      
      return true;
    } catch (error) {
      console.error('Error deleting routing:', error);
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },
  
  // Duplicate a routing
  duplicateRouting: async (id: UUID, userId: UUID) => {
    set({ isLoading: true, error: null });
    
    try {
      // 1. Get the original routing
      const { data: originalRouting, error: fetchError } = await supabase
        .from('routings')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // 2. Create a new routing with copied values
      const newRouting: RoutingCreate = {
        routing_name: `${originalRouting.routing_name} (Copy)`,
        project_id: originalRouting.project_id,
        bom_id: originalRouting.bom_id,
        assembly_id: originalRouting.assembly_id,
        version: originalRouting.version,
        notes: originalRouting.notes,
        status: 'draft', // Always start as draft
        created_by: userId
      };
      
      const { data: newRoutingData, error: createError } = await supabase
        .from('routings')
        .insert(newRouting)
        .select('id')
        .single();
      
      if (createError) throw createError;
      
      // 3. Get the original operations
      const { data: originalOperations, error: opsError } = await supabase
        .from('routing_operations')
        .select('*')
        .eq('routing_id', id);
      
      if (opsError) throw opsError;
      
      // 4. Duplicate the operations for the new routing
      if (originalOperations && originalOperations.length > 0) {
        const newOperations = originalOperations.map(op => ({
          routing_id: newRoutingData.id,
          sequence_no: op.sequence_no,
          operation_id: op.operation_id,
          workstation_id: op.workstation_id,
          process_time: op.process_time,
          setup_time: op.setup_time,
          queue_time: op.queue_time,
          is_subcontracted: op.is_subcontracted,
          subcontractor_id: op.subcontractor_id,
          qc_required: op.qc_required,
          operation_cost: op.operation_cost,
          created_by: userId
        }));
        
        const { error: insertOpsError } = await supabase
          .from('routing_operations')
          .insert(newOperations);
        
        if (insertOpsError) throw insertOpsError;
      }
      
      // 5. Refresh routings list
      await get().fetchRoutings();
      
      set({ isLoading: false });
      return newRoutingData.id;
    } catch (error) {
      console.error('Error duplicating routing:', error);
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },
  
  // Fetch routing operations
  fetchRoutingOperations: async (routingId: UUID) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('routing_operations_with_details')
        .select('*')
        .eq('routing_id', routingId)
        .order('sequence_no', { ascending: true });
      
      if (error) throw error;
      
      set({ routingOperations: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching routing operations:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },
  
  // Create routing operation
  createRoutingOperation: async (operation: RoutingOperationCreate) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('routing_operations')
        .insert(operation)
        .select('id')
        .single();
      
      if (error) throw error;
      
      // Refresh operations list
      await get().fetchRoutingOperations(operation.routing_id);
      
      set({ isLoading: false });
      return data.id;
    } catch (error) {
      console.error('Error creating routing operation:', error);
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },
  
  // Update routing operation
  updateRoutingOperation: async (id: UUID, updates: Partial<RoutingOperation>) => {
    set({ isLoading: true, error: null });
    
    try {
      // 1. Get the routing ID first for refreshing later
      const { data: opData, error: fetchError } = await supabase
        .from('routing_operations')
        .select('routing_id')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // 2. Update the operation
      const { error } = await supabase
        .from('routing_operations')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      
      if (error) throw error;
      
      // 3. Refresh operations list
      await get().fetchRoutingOperations(opData.routing_id);
      
      set({ isLoading: false });
      return true;
    } catch (error) {
      console.error('Error updating routing operation:', error);
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },
  
  // Delete routing operation
  deleteRoutingOperation: async (id: UUID) => {
    set({ isLoading: true, error: null });
    
    try {
      // 1. Get the routing ID first for refreshing later
      const { data: opData, error: fetchError } = await supabase
        .from('routing_operations')
        .select('routing_id')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // 2. Delete the operation
      const { error } = await supabase
        .from('routing_operations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // 3. Refresh operations list
      await get().fetchRoutingOperations(opData.routing_id);
      
      set({ isLoading: false });
      return true;
    } catch (error) {
      console.error('Error deleting routing operation:', error);
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },
  
  // Reorder routing operations
  reorderRoutingOperations: async (routingId: UUID, sequenceUpdates: { id: UUID, sequence_no: number }[]) => {
    set({ isLoading: true, error: null });
    
    try {
      // Start a transaction
      const updates = sequenceUpdates.map(update => 
        supabase
          .from('routing_operations')
          .update({ sequence_no: update.sequence_no, updated_at: new Date().toISOString() })
          .eq('id', update.id)
      );
      
      // Execute all updates
      await Promise.all(updates);
      
      // Refresh operations list
      await get().fetchRoutingOperations(routingId);
      
      set({ isLoading: false });
      return true;
    } catch (error) {
      console.error('Error reordering routing operations:', error);
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },
  
  // Component Operations CRUD
  fetchComponentOperationsByRouting: async (routingId: UUID): Promise<RoutingComponentOperationWithDetails[]> => {
    try {
      set({ isLoading: true });
      
      const { data, error } = await supabase
        .from('routing_component_operations_with_details')
        .select('*')
        .eq('routing_id', routingId)
        .order('component_name', { ascending: true })
        .order('sequence_no', { ascending: true });
        
      if (error) throw error;
      
      set({ componentOperations: data || [], isLoading: false });
      return data || [];
    } catch (error) {
      console.error('Error fetching component operations:', error);
      set({ error: (error as Error).message, isLoading: false });
      return [];
    }
  },
  
  createComponentOperation: async (data: RoutingComponentOperationCreate): Promise<UUID | null> => {
    try {
      set({ isLoading: true });
      
      const { data: result, error } = await supabase
        .from('routing_component_operations')
        .insert(data)
        .select('id')
        .single();
        
      if (error) throw error;
      
      // Refresh the component operations
      if (result?.id) {
        await get().fetchComponentOperationsByRouting(data.routing_id);
      }
      
      set({ isLoading: false });
      return result?.id || null;
    } catch (error) {
      console.error('Error creating component operation:', error);
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },
  
  deleteComponentOperation: async (id: UUID): Promise<boolean> => {
    try {
      set({ isLoading: true });
      
      // First get the routing_id so we can refresh afterward
      const componentOp = get().componentOperations.find(op => op.id === id);
      if (!componentOp) throw new Error('Component operation not found');
      
      const { error } = await supabase
        .from('routing_component_operations')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      // Refresh the component operations
      await get().fetchComponentOperationsByRouting(componentOp.routing_id);
      
      set({ isLoading: false });
      return true;
    } catch (error) {
      console.error('Error deleting component operation:', error);
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },
  
  // Helper method to group operations by component
  getOperationsGroupedByComponent: (routingId: UUID): ComponentWithOperations[] => {
    const componentOps = get().componentOperations;
    const operations = get().routingOperations;
    
    if (!componentOps.length || !operations.length) return [];
    
    // Create a map to group operations by component
    const componentMap = new Map<UUID, ComponentWithOperations>();
    
    // First, populate the map with unique components
    componentOps.forEach(cop => {
      if (!componentMap.has(cop.component_id)) {
        componentMap.set(cop.component_id, {
          component_id: cop.component_id,
          component_name: cop.component_name,
          drawing_number: cop.drawing_number,
          material: cop.material,
          quantity: cop.quantity,
          quantity_unit: cop.quantity_unit,
          operations: []
        });
      }
    });
    
    // Then add the operations to each component
    componentOps.forEach(cop => {
      const operation = operations.find(op => op.id === cop.operation_id);
      if (operation && componentMap.has(cop.component_id)) {
        const component = componentMap.get(cop.component_id);
        if (component) {
          component.operations.push(operation as RoutingOperationWithDetails);
        }
      }
    });
    
    // Convert the map to an array
    return Array.from(componentMap.values());
  },
  
  // State management
  clearCurrentRouting: () => {
    set({ currentRouting: null, routingOperations: [], componentOperations: [] });
  },
  
  setError: (error: string | null) => {
    set({ error });
  }
}));
